// Раздел «Очередь уведомлений»: что движок процессов отправил людям и что застряло.
// ТОЛЬКО ЧТЕНИЕ — ручек записи в /notify не существует.
//
// Зачем этот экран. Движок складывает уведомления в очередь, а отправляет отдельный
// разборщик, и между «захотел сказать» и «человек получил» три двери: переменные
// окружения канала, адрес человека в зеркале, сам Telegram/SMTP. За каждой строка
// может встать, и до этого экрана застревание было видно только из консоли сервера.
//
// ТЕКСТОВ УВЕДОМЛЕНИЙ ЗДЕСЬ НЕТ, и это граница ядра, а не выбор экрана: в теле — текст
// чужого задания. Показаны канал, кому, когда, состояние и причина; этого достаточно,
// чтобы увидеть «дошло или застряло», и недостаточно, чтобы читать чужую работу.

import { useMemo, useState } from 'react'

import type { Адресат, Статус, СтрокаОчереди } from '../api/notify'
import { useNotifyRefs, useОчередь } from '../api/notify'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, pluralN } from '../lib/format'

const РАЗМЕР_СТРАНИЦЫ = 50

/** Отбор по состоянию: код ядра → как оно называется человеку. Порядок — как в жизни:
 *  сначала то, что требует действий, потом остальное. */
const ВИДЫ: { код: string; имя: string }[] = [
  { код: 'stuck', имя: 'застряло' },
  { код: 'unreachable', имя: 'недостижим' },
  { код: 'config', имя: 'ждёт настройки' },
  { код: 'waiting', имя: 'ждёт отправки' },
  { код: 'sent', имя: 'отправлено' },
  { код: 'screen', имя: 'показ в CRM' },
]

function видСтатуса(s: Статус): { ярлык: string; класс: string } {
  switch (s) {
    case 'отправлено': return { ярлык: 'отправлено', класс: 'badge won' }
    case 'застряло': return { ярлык: 'застряло', класс: 'badge lose' }
    case 'недостижим': return { ярлык: 'недостижим', класс: 'badge lose' }
    case 'ждёт_настройки': return { ярлык: 'ждёт настройки', класс: 'badge work' }
    case 'на_экране': return { ярлык: 'показ в CRM', класс: 'badge' }
    default: return { ярлык: 'ждёт', класс: 'badge' }
  }
}

export default function NotifyQueue() {
  const спр = useNotifyRefs()
  const с = спр.data
  const [канал, setКанал] = useState('')
  const [вид, setВид] = useState('')
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    channel: канал || undefined,
    kind: вид || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [канал, вид, start])
  const запрос = useОчередь(фильтры)
  const д = запрос.data
  const отобрано = Boolean(канал || вид)

  // Числа для адресной оговорки — из того же ответа, что и всё остальное.
  const действующие = с?.addresses.active

  return (
    <>
      <div className="page-head">
        <h1>Очередь уведомлений</h1>
        <span className="spacer" />
        <span className="hint">
          {с ? `всего ${pluralN(с.totals.total, 'строка', 'строки', 'строк')}:`
            + ` отправлено ${с.totals.sent}, ждёт ${с.totals.waiting},`
            + ` застряло ${с.totals.stuck}` : ''}
        </span>
      </div>

      {с && !с.live && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <b>Боевая отправка выключена.</b> Переменная NOTIFY_LIVE не названа: разборщик
          считает, что бы ушло, но живому человеку не уходит ничего. Включает владелец
          на сервере ядра — это имя переменной, а не кнопка здесь.
        </div>
      )}

      {с && с.caveats.length > 0 && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <b>Как это читать.</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {с.caveats.map((т) => (
              <li key={т}>{т}</li>
            ))}
          </ul>
          {действующие && (
            <div style={{ marginTop: 6 }}>
              Telegram-адрес собран у {действующие.with_chat_id} из{' '}
              {pluralN(действующие.total, 'действующего сотрудника',
                'действующих сотрудников', 'действующих сотрудников')}; без него{' '}
              {pluralN(действующие.no_chat_id, 'человек', 'человека', 'человек')}
              {действующие.only_nick > 0
                ? <> (у {действующие.only_nick} известен только ник — адресом он не
                  является)</> : ''}, им телеграм-уведомление не доставить. Почта есть
              у {действующие.with_email}.
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="notify-queue">
          <div className="f" style={{ minWidth: 160 }}>
            <label htmlFor="nqch">Канал</label>
            <select id="nqch" value={канал}
                    onChange={(e) => { setStart(0); setКанал(e.target.value) }}
                    disabled={!с}>
              <option value="">любой</option>
              {(с?.channels ?? []).map((к) => (
                <option key={к.channel ?? ''} value={к.channel ?? ''}>
                  {к.channel} ({к.total})
                </option>
              ))}
            </select>
          </div>
          <div className="f" style={{ minWidth: 180 }}>
            <label htmlFor="nqkind">Состояние</label>
            <select id="nqkind" value={вид}
                    onChange={(e) => { setStart(0); setВид(e.target.value) }}>
              <option value="">любое</option>
              {ВИДЫ.map((в) => (
                <option key={в.код} value={в.код}>{в.имя}</option>
              ))}
            </select>
          </div>
        </FilterBar>
        {д?.note && <div className="hint" style={{ marginTop: 8 }}>{д.note}</div>}
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">Когда</th>
                  <th className="nowrap">Канал</th>
                  <th>Кому</th>
                  <th className="nowrap">Состояние</th>
                  <th className="num">Попыток</th>
                  <th className="nowrap">Следующая попытка</th>
                  <th>Причина</th>
                </tr>
              </thead>
              <tbody>
                {(д?.rows ?? []).map((r) => <Строка key={r.id} r={r} />)}
                {д && д.rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty">
                      {отобрано ? (
                        <div className="empty-title">Под эти условия строк в очереди нет</div>
                      ) : (
                        <>
                          <div className="empty-title">Очередь пуста</div>
                          <div className="empty-hint">
                            Движок ещё не захотел никому ничего сказать. Это не поломка:
                            строки появляются, когда процесс доходит до шага уведомления.
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {д && д.total > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={start === 0 || запрос.isFetching}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, д.total)} из {д.total}
              </span>
              <button disabled={д.next === null || запрос.isFetching}
                      onClick={() => setStart(д.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}

function Кому({ кому }: { кому: Адресат[] }) {
  if (!кому.length) return <span className="muted">адресаты не названы</span>
  return (
    <>
      {кому.map((а, i) => (
        <span key={`${а.who}`} className="nowrap">
          {i > 0 && ', '}
          {а.name ?? fmtText(String(а.who))}
          {а.active === false && (
            <span className="badge lose" style={{ marginLeft: 4 }}>не работает</span>
          )}
        </span>
      ))}
    </>
  )
}

function Строка({ r }: { r: СтрокаОчереди }) {
  const с = видСтатуса(r.status)
  return (
    <tr>
      <td className="nowrap muted" data-label="Когда">{fmtDate(r.at, true)}</td>
      <td className="nowrap" data-label="Канал">{fmtText(r.channel)}</td>
      <td data-label="Кому"><Кому кому={r.to} /></td>
      <td className="nowrap" data-label="Состояние">
        <span className={с.класс}
              title={r.sent_at ? `ушло ${fmtDate(r.sent_at, true)}` : undefined}>
          {с.ярлык}
        </span>
        {r.delivered_n > 0 && !r.sent_at && (
          // Частичная доставка: кому-то ушло, строка ждёт остальных. Молчать об этом
          // нельзя — «застряло» без пометки читалось бы как «не ушло никому».
          <div className="badge" title="части адресатов уже доставлено, строка ждёт остальных">
            доставлено {r.delivered_n}
          </div>
        )}
      </td>
      <td className="num" data-label="Попыток">{r.attempts}</td>
      <td className="nowrap muted" data-label="Следующая попытка">
        {/* У отправленного и застрявшего следующей попытки нет: у первого незачем,
            у второго попытки кончились. У ждущего без времени она «хоть сейчас» —
            прочерк там читался бы как «не запланирована». */}
        {r.sent_at || r.status === 'застряло' || r.status === 'на_экране'
          ? '—' : r.next_try_at ? fmtDate(r.next_try_at, true) : 'хоть сейчас'}
      </td>
      <td data-label="Причина">
        {r.error
          ? <span className="hint">{r.error}</span>
          : <span className="muted">—</span>}
      </td>
    </tr>
  )
}
